// Tab bar shared by the left and right sidebar.
import { h } from './dom.js';

export interface TabDef {
  id: string;
  label: string;
  title?: string;
  element: HTMLElement;
  onShow?(): void;
}

export class TabbedPanel {
  readonly el: HTMLElement;
  private activeId: string;
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private readonly tabs: TabDef[];

  constructor(tabs: TabDef[], storageKey: string) {
    if (tabs.length === 0) throw new Error('TabbedPanel needs at least one tab');
    this.tabs = tabs;
    const buttons: HTMLElement[] = [];
    const body = h('div', { class: 'tab-body' });
    for (const tab of tabs) {
      const button = h('button', {
        class: 'tab-button',
        text: tab.label,
        type: 'button',
        ...(tab.title ? { title: tab.title } : {}),
      });
      button.addEventListener('click', () => this.show(tab.id));
      this.buttons.set(tab.id, button);
      buttons.push(button);
      tab.element.hidden = true;
      body.append(tab.element);
    }
    const stored = window.localStorage.getItem(storageKey);
    this.activeId = tabs.find((tab) => tab.id === stored)?.id ?? tabs[0]!.id;
    this.el = h('div', { class: 'tabbed-panel' }, [h('div', { class: 'tab-bar' }, buttons), body]);
    this.apply();
    this.el.addEventListener('click', () => {
      window.localStorage.setItem(storageKey, this.activeId);
    });
  }

  get active(): string {
    return this.activeId;
  }

  show(id: string): void {
    if (!this.buttons.has(id)) return;
    this.activeId = id;
    this.apply();
  }

  private apply(): void {
    for (const tab of this.tabs) {
      const isActive = tab.id === this.activeId;
      this.buttons.get(tab.id)?.classList.toggle('active', isActive);
      tab.element.hidden = !isActive;
      if (isActive) tab.onShow?.();
    }
  }
}
